import { useEffect, useRef } from "react";
import Select, { components, createFilter } from "react-select";

const SELECIONAR_TODOS = "__selecionar_todos__";

const filtroPadrao = createFilter();

const etiquetas = (wrapper) => wrapper?.querySelector('.select-multiplo__value-container');

const Opcao = (props) => {
    const { todosSelecionados, algumSelecionado } = props.selectProps;
    const especial = props.data.value === SELECIONAR_TODOS;
    const marcado = especial ? todosSelecionados : props.isSelected;

    return (
        <components.Option {...props}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                    type="checkbox"
                    checked={marcado}
                    readOnly
                    tabIndex={-1}
                    ref={(el) => { if (el) el.indeterminate = especial && algumSelecionado && !todosSelecionados; }}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                />
                <span>{especial ? (todosSelecionados ? "Desmarcar todos" : "Selecionar todos") : props.label}</span>
            </div>
        </components.Option>
    );
};

// Modo somente leitura: mostra as etiquetas sem permitir remover, abrir a lista ou limpar
const componentesLeitura = {
    Option: Opcao,
    MultiValueRemove: () => null,
    DropdownIndicator: null,
    IndicatorSeparator: null,
    ClearIndicator: null,
};

const estilos = {
    container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
    control: (base) => ({ ...base, minHeight: 38, flexWrap: 'nowrap', alignItems: 'flex-start' }),
    // Etiquetas quebram linha e, passando da altura máxima, rolam verticalmente sem levar o X e a seta junto
    valueContainer: (base) => ({
        ...base,
        minHeight: 36,
        maxHeight: 62,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#ced4da', borderRadius: 2 },
    }),
    multiValue: (base) => ({ ...base, minWidth: 0 }),
    indicatorsContainer: (base) => ({ ...base, flexShrink: 0, height: 36 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => state.data.value === SELECIONAR_TODOS
        ? {
            ...base,
            fontWeight: 600,
            color: '#212529',
            backgroundColor: state.isFocused ? '#e9ecef' : '#f8f9fa',
            borderBottom: '2px solid #ced4da',
            marginTop: -4,
            paddingTop: 12,
            paddingBottom: 12,
        }
        : { ...base, color: '#212529', backgroundColor: state.isFocused ? '#f1f3f5' : 'white' },
};

// Converte uma lista da API em opções { label, value }
export const paraOpcoes = (lista, campoValor, campoTexto) =>
    (lista || []).map((item) => ({ value: item[campoValor], label: item[campoTexto] }));

// Adapta handlers que esperam um evento com a lista de chaves em e.target.value
export const comoEvento = (handler) => (selecionados) =>
    handler({ target: { value: selecionados.map((opcao) => opcao.value) } });

export default function SelectMultiplo({
    options = [],
    value = [],
    onChange,
    placeholder = "Selecione",
    isDisabled = false,
    readOnly = false,
    className
}) {

    // value aceita opções { label, value } ou apenas as chaves selecionadas
    const selecionadas = (value || [])
        .map((item) => item !== null && typeof item === 'object' ? item : options.find((opcao) => opcao.value === item))
        .filter(Boolean);

    const todosSelecionados = options.length > 0 && selecionadas.length === options.length;
    const algumSelecionado = selecionadas.length > 0;

    const wrapperRef = useRef(null);

    // Ao adicionar uma opção, rola até o final para mostrar a última escolhida
    useEffect(() => {
        const container = etiquetas(wrapperRef.current);
        if (container && !readOnly) container.scrollTop = container.scrollHeight;
    }, [selecionadas.length, readOnly]);

    return (
        // className fica na div externa (ex.: max-width); a interna, com width 0 + minWidth 100%,
        // ocupa todo o espaço disponível sem que as etiquetas aumentem a largura da coluna
        <div className={className}>
            <div ref={wrapperRef} style={{ width: 0, minWidth: '100%' }}>
                <Select
                    isMulti
                    options={options.length > 0 && !readOnly ? [{ label: "Selecionar todos", value: SELECIONAR_TODOS }, ...options] : options}
                    value={selecionadas}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    isSearchable={!readOnly}
                    isClearable={!readOnly}
                    menuIsOpen={readOnly ? false : undefined}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    filterOption={(opcao, busca) => opcao.value === SELECIONAR_TODOS ? !busca : filtroPadrao(opcao, busca)}
                    noOptionsMessage={() => "Nenhuma opção"}
                    components={readOnly ? componentesLeitura : { Option: Opcao }}
                    styles={estilos}
                    menuPortalTarget={document.body}
                    className="select-multiplo"
                    classNamePrefix="select-multiplo"
                    todosSelecionados={todosSelecionados}
                    algumSelecionado={algumSelecionado}
                    onChange={(selecionados, meta) => {
                        if (readOnly) return;
                        if (meta.option?.value === SELECIONAR_TODOS) {
                            onChange(todosSelecionados ? [] : options);
                        } else {
                            onChange(selecionados || []);
                        }
                    }}
                />
            </div>
        </div>
    );
}
